<template>
  <div class="vasion-calc-height vasion-white-background">
    <Loading
      class="vasion-loading-indicator"
      :active.sync="isLoading"
      :is-full-page="true"
      :color="loaderColor"
      loader="dots"
      :background-color="loaderBackgroundColor"
    />
    <div class="section-container">
      <div class="section-header md-layout">
        <div class="md-layout-item">
          <h1 class="vasion-page-title">
            Global Settings
          </h1>
        </div>
        <div class="md-layout-item vasion-flex-end">
          <VasionButton
            id="btn-save"
            :classProp="'primary'"
            :isDisabled="disableSaveButton"
            @vasionButtonClicked="saveGlobalSettings()"
          >
            Save
          </VasionButton>
        </div>
      </div>
      <div class="section-content">
        <div class="form-container">
          <div class="input-container mb-3">
            <VasionInput
              id="workflow-web-url"
              v-model="formData.workflowWebURL.value"
              name="workflowWebURL"
              title="Workflow Web URL"
              class="input-style"
              inputType="top-white"
              specialType="url"
              @input="markAsDirty(); checkInput('workflowWebURL', formData.workflowWebURL.value)"
              @notValid="fieldNotValid('workflowWebURL')"
              @isValid="fieldIsValid('workflowWebURL')"
            />
            <span v-if="invalidFields.includes('workflowWebURL')" class="error-message">You must enter a valid URL.</span>
            <span v-else-if="emptyFields.includes('workflowWebURL')" class="error-message">The value of this field must not be empty.</span>
          </div>
          <div class="input-container mb-3">
            <VasionInput
              id="max-search-results"
              v-model="formData.maxSearchResults.value"
              name="maxSearchResults"
              title="Max Search Results"
              class="input-style"
              inputType="top-white"
              specialType="number"
              @input="markAsDirty(); checkInput('maxSearchResults', formData.maxSearchResults.value)"
            />
            <span v-if="invalidFields.includes('maxSearchResults')" class="error-message">You must enter a valid number.</span>
            <span v-else-if="emptyFields.includes('maxSearchResults')" class="error-message">The value of this field must not be empty.</span>
          </div>
          <div class="input-container mb-3" v-if="bridgeEnabled">
            <VasionInput
              id="vasion-automate-url"
              v-model="formData.vasionAutomateUrl.value"
              name="vasionAutomateUrl"
              title="Vasion Automate URL"
              class="input-style"
              inputType="top-white"
              specialType="url"
              @input="markAsDirty(); checkInput('vasionAutomateUrl', formData.vasionAutomateUrl.value)"
              @notValid="fieldNotValid('vasionAutomateUrl')"
              @isValid="fieldIsValid('vasionAutomateUrl')"
            />
            <span v-if="invalidFields.includes('vasionAutomateUrl')" class="error-message">You must enter a valid URL.</span>
          </div>
          <div class="input-container mb-3" v-if="bridgeEnabled">
            <VasionInput
              id="vasion-print-url"
              v-model="formData.vasionPrintUrl.value"
              name="vasionPrintUrl"
              title="Vasion Print URL"
              class="input-style"
              inputType="top-white"
              specialType="url"
              @input="markAsDirty(); checkInput('vasionPrintUrl', formData.vasionPrintUrl.value)"
              @notValid="fieldNotValid('vasionPrintUrl')"
              @isValid="fieldIsValid('vasionPrintUrl')"
            />
            <span v-if="invalidFields.includes('vasionPrintUrl')" class="error-message">You must enter a valid URL.</span>
          </div>
        </div>
      </div>
    </div>
    <VasionGeneralModal
      id="mdl-prevent-exit-without-save"
      :confirmButtonText="'YES'"
      :rejectButtonText="'NO'"
      :modalType="'message'"
      :buttonGroup="'center'"
      :message="'Your changes have not been saved, are you sure you want to cancel?'"
      :sync="showLeaveDialog"
      @noButtonClick="cancelLeave()"
      @confirmButtonClick="doLeave()"
    />
    <VasionSnackbar
      id="email-settings-snack"
      :showSnackbarBool.sync="showSnackbar"
      :snackbarImage="snackbarImage"
      :snackbarSubTitle="snackbarSubTitle"
      :snackbarTitle="snackbarTitle"
    />
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay'
import cloneDeep from 'lodash/cloneDeep'
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'

export default {
  name: 'GlobalSettings',
  components: {
    Loading,
  },
  beforeRouteLeave(to, from, next) {
    if (this.isDirty && !this.routeTo) {
      this.routeTo = to
      this.toggleLeaveDialog()
    } else {
      this.routeTo = null
      next()
    }
  },
  data: function () {
    return {
      bridgeEnabled: false,
      emptyFields: [],
      formData: {
        workflowWebURL: {
          value: '',
          backendName: 'WFURL',
        },
        maxSearchResults: {
          value: '',
          backendName: 'MaxSearchResult',
        },
        vasionAutomateUrl: {
          value: '',
          backendName: 'VasionAutomateURL',
        },
        vasionPrintUrl: {
          value: '',
          backendName: 'VasionPrintURL',
        },
      },
      formDataInitialState: {},
      invalidFields: [],
      isDirty: false,
      isLoading: false,
      loaderBackgroundColor: loaderBackgroundColor,
      loaderColor: loaderColor,
      showLeaveDialog: false,
      showSnackbar: false,
      snackbarImage: false,
      snackbarSubTitle: '',
      snackbarTitle: '',
    }
  },
  computed: {
    disableSaveButton() { return !this.isDirty || this.emptyFields.length !== 0 || this.invalidFields.length !== 0 },
  },
  async created() {
    this.isLoading = true

    await this.getGlobalSettings()
    this.formDataInitialState = cloneDeep(this.formData)

    this.isLoading = false
  },
  methods: {
    cancelLeave() {
      this.routeTo = null
      this.toggleLeaveDialog()
    },
    checkInput(fieldName, fieldValue) {
      if (fieldValue === "" && fieldName !== "vasionPrintUrl" && fieldName !== "vasionAutomateUrl") {
        if(!this.emptyFields.includes(fieldName)) { this.emptyFields.push(fieldName) }
      }else {
        this.emptyFields = this.emptyFields.filter(element => element !== fieldName);
      }
    },
    doLeave() {
      this.toggleLeaveDialog()
      this.$router.push({ path: this.routeTo.path })
    },
    fieldNotValid(fieldName) {
      if (!this.invalidFields.includes(fieldName)) {
        this.invalidFields.push(fieldName)
      }
    },
    fieldIsValid(fieldName) {
      this.invalidFields = this.invalidFields.filter(element => element !== fieldName);
    },
    async getGlobalSettings() {
      this.formData.workflowWebURL.value = await this.$store.dispatch('common/getConfig', this.formData.workflowWebURL.backendName)
      this.formData.maxSearchResults.value = await this.$store.dispatch('common/getConfig', this.formData.maxSearchResults.backendName)
      this.bridgeEnabled = this.$store.state.systemSettings.featureFlags.vasionAutomateBridge
      this.formData.vasionAutomateUrl.value = this.$store.state.systemSettings.vasionAutomateURL
      this.formData.vasionPrintUrl.value = this.$store.state.systemSettings.vasionPrintURL
    },
    async getModifiedVariables(initialState, currentState) {
      let changes = {}
      for (const key in initialState) {
        if (initialState[key].value !== currentState[key].value) {
          changes[key] = currentState[key]
        }
      }
      return changes
    },
    markAsClean() {
      this.isDirty = false
    },
    markAsDirty() {
      this.isDirty = true
    },
    async saveGlobalSettings() {
      this.isLoading = true

      const changes = await this.getModifiedVariables(this.formDataInitialState, this.formData)

      try {
        for (const key in changes) {
          const payload = {
            sConfigName: changes[key].backendName,
            sConfigValue: changes[key].value,
          }
          await this.$store.dispatch('common/setConfig', payload)
        }
      } catch (error) {
        this.isLoading = false
        this.snackbarTitle = 'Error'
        this.snackbarSubTitle = 'Error while saving global settings.'
        this.snackbarImage = false
        this.showSnackbar = true
      }
      this.isLoading = false
      this.snackbarTitle = 'Success'
      this.snackbarSubTitle = 'Global settings saved.'
      this.snackbarImage = true
      this.showSnackbar = true

      this.formDataInitialState = cloneDeep(this.formData)
      this.markAsClean()
    },
    toggleLeaveDialog() { this.showLeaveDialog = !this.showLeaveDialog },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';

.section-container {
  margin-left: 1rem;
  margin-top: 1rem;
  margin-right: 1rem;
}
.section-header {
  min-height: 68px;
}
.section-content {
  height: calc(100vh - 140px);
  overflow: auto;
}
.form-container {
  width: 100%;
}
.mb-3 {
  margin-bottom: 16px;
}
.error-message {
  color: $error-red;
  font-weight: 200;
}
// prevent side bar
@media (min-width: 950px) and (max-width: 991.98px) {
  .form-container {
    width: 600px;
  }
}
// lg: 992px
@media (min-width: $breakpoint-lg) {
  .form-container {
    width: 600px;
  }
}
</style>
